import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { portalDs } from 'src/components/portal-design-system';
import { useGetEtlMartVerificationQuery } from 'src/redux/operations/operations.endpoints';
import type { EtlHealthStatusFilter } from '../types/operations.types';
import { formatNumber } from '../helpers/etl-health.helpers';
import { EtlDetailTableV2 } from './etl-detail-table-v2';
import { EtlHealthStatusPillV2 } from './etl-health-status-pill-v2';
import { EtlRunsPagination } from './etl-runs-pagination';

const HEALTH_DAYS = 30;
export const MART_VERIFICATION_PAGE_SIZE = 10;

type Props = {
  selectedDate: string | null;
  refreshToken?: number;
};

export function MartVerificationPanelV2({ selectedDate, refreshToken = 0 }: Props) {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<EtlHealthStatusFilter>('issues');

  useEffect(() => {
    setPage(1);
  }, [selectedDate, statusFilter]);

  const martQuery = useGetEtlMartVerificationQuery({
    days: HEALTH_DAYS,
    page,
    page_size: MART_VERIFICATION_PAGE_SIZE,
    status_filter: statusFilter,
    ...(selectedDate ? { date: selectedDate } : {}),
    ...(refreshToken > 0 ? { refresh: 1 as const } : {}),
  });

  const rows = martQuery.data?.rows ?? [];
  const totalCount = martQuery.data?.total_count ?? 0;
  const totalPages = martQuery.data?.total_pages ?? 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1.5">
          <span className={portalDs.filterLabel}>Show</span>
          <select
            className={cn(portalDs.select, 'min-w-[160px]')}
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value as EtlHealthStatusFilter);
            }}
          >
            <option value="issues">Warnings and failures</option>
            <option value="all">All statuses</option>
          </select>
        </label>
      </div>

      {(martQuery.isLoading || martQuery.isFetching) && (
        <div className={cn('flex items-center gap-2 py-4', portalDs.bodySm)} role="status">
          <Loader2 className="size-4 animate-spin text-[#9CA3AF]" />
          Loading mart verification checks…
        </div>
      )}

      {martQuery.isError && !martQuery.isLoading && (
        <p className={cn(portalDs.bodySm, 'text-[#B91C1C]')}>
          Could not load mart verification checks.
        </p>
      )}

      {!martQuery.isLoading && !martQuery.isError && (
        <>
          <EtlDetailTableV2
            rows={rows}
            columns={[
              { id: 'date', header: 'Date', accessor: (row) => row.date },
              { id: 'datasource', header: 'Datasource', accessor: (row) => row.datasource },
              { id: 'connector', header: 'Connector', accessor: (row) => row.connector },
              { id: 'check_type', header: 'Check type', accessor: (row) => row.check_type },
              {
                id: 'value',
                header: 'Value',
                accessor: (row) => row.value,
                align: 'right',
                render: (row) => formatNumber(row.value),
              },
              {
                id: 'threshold',
                header: 'Threshold',
                accessor: (row) => row.threshold,
                align: 'right',
                render: (row) => formatNumber(row.threshold),
              },
              {
                id: 'status',
                header: 'Status',
                accessor: (row) => row.status,
                render: (row) => <EtlHealthStatusPillV2 status={row.status} />,
              },
              {
                id: 'ingestion_ts',
                header: 'Ingestion ts',
                accessor: (row) => row.ingestion_ts,
              },
            ]}
            emptyMessage={
              statusFilter === 'issues'
                ? 'No warnings or failures for the selected period.'
                : 'No mart verification checks for the selected period.'
            }
            hideRowCount
          />

          <EtlRunsPagination
            page={page}
            totalPages={totalPages}
            totalObjects={totalCount}
            pageSize={MART_VERIFICATION_PAGE_SIZE}
            onPageChange={setPage}
            disabled={martQuery.isFetching}
          />

          <p className={portalDs.rowCount}>
            Sorted by most recent first.
            {' '}
            {totalCount > 0
              ? `${totalCount} matching check${totalCount === 1 ? '' : 's'} in the last ${HEALTH_DAYS} days.`
              : `No matching checks in the last ${HEALTH_DAYS} days.`}
          </p>
        </>
      )}
    </div>
  );
}
